import type { P3 } from './types';

/**
 * DOM access that behaves the same on a browser `Document` and on
 * `@xmldom/xmldom`. Deliberately no `querySelector` and no `.children`
 * (xmldom has neither). Element lookups go through `getElementsByTagNameNS('*', …)`,
 * which matches by LOCAL name in any namespace — the same thing an un-namespaced
 * CSS type selector matched in an XML document, so prefixed files keep working.
 */
type DomRoot = Document | Element;

function toArray(list: HTMLCollectionOf<Element> | NodeListOf<Element>): Element[] {
  const out: Element[] = [];
  for (let i = 0; i < list.length; i++) {
    const el = list.item(i);
    if (el) out.push(el);
  }
  return out;
}

export function elementChildren(el: Element): Element[] {
  const out: Element[] = [];
  for (let n = el.firstChild; n; n = n.nextSibling) if (n.nodeType === 1) out.push(n as Element);
  return out;
}

/** Direct children with a given local name. */
export const childrenNamed = (el: Element, name: string): Element[] =>
  elementChildren(el).filter((c) => c.localName === name);

export const firstChild = (el: Element, name: string): Element | null => childrenNamed(el, name)[0] ?? null;

/** Every descendant with a given local name, document order (`querySelectorAll('Name')`). */
export const descendants = (root: DomRoot, name: string): Element[] =>
  toArray(root.getElementsByTagNameNS('*', name));

/** First descendant with a given local name (`querySelector('Name')`). */
export const firstDescendant = (root: DomRoot, name: string): Element | null => descendants(root, name)[0] ?? null;

export const parentName = (el: Element): string | null => {
  const p = el.parentNode;
  return p && p.nodeType === 1 ? (p as Element).localName : null;
};

/** `querySelectorAll('Parent > Name')`. */
export const childPath = (root: DomRoot, parent: string, name: string): Element[] =>
  descendants(root, name).filter((e) => parentName(e) === parent);

export const textOf = (el: Element | null): string | null => (el?.textContent ? el.textContent : null);

export const numAttr = (el: Element, name: string): number | null => {
  const v = el.getAttribute(name);
  if (v === null) return null;
  if (/^inf$/i.test(v.trim())) return Infinity;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

/** "northing easting elevation" → `[easting, northing, elevation]` (Z-up). */
export const parsePoint = (text: string): P3 => {
  const parts = text.trim().split(/\s+/).map(Number);
  return [parts[1], parts[0], parts[2] ?? 0];
};
